import React, { useState, useRef, useEffect, useCallback } from "react";
import styled from "styled-components";
import dialOuter from "./dial-outer.png";

interface DialProps {
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
  label: string;
  size?: number;
  color?: string;
  disabled?: boolean;
  valueFormatter?: (value: number) => string;
  variant?: "default" | "small";
  hideValue?: boolean;
  showCustomStyle?: boolean;
}

const Dial: React.FC<DialProps> = ({
  value,
  min,
  max,
  onChange,
  label,
  size,
  color: _color = "#61dafb",
  disabled = false,
  valueFormatter = (val) => val.toFixed(2),
  variant = "default",
  hideValue = false,
  showCustomStyle = false,
}) => {
  // Set size based on variant if size not explicitly provided
  const dialSize = size || (variant === "small" ? 32 : 40);
  const dialRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const [currentValue, setCurrentValue] = useState(value);

  // Calculate the normalized value (0-1)
  const normalizedValue = (currentValue - min) / (max - min);

  // Calculate rotation angle based on normalized value
  const getRotationAngle = () => {
    // Map 0-1 to -150 to 150 degrees (300 degree range)
    return normalizedValue * 300 - 150;
  };

  // Handle mouse/touch down event
  const handleDragStart = (clientY: number) => {
    if (disabled) return;

    setIsDragging(true);
    setStartY(clientY);
  };

  // Handle mouse/touch move event
  const handleDragMove = useCallback(
    (clientY: number) => {
      if (!isDragging || disabled) return;

      // Calculate vertical movement
      const deltaY = startY - clientY;

      // Sensitivity factor - how much to change per pixel of movement
      // Adjust this value to make the dial more or less sensitive
      const sensitivity = (max - min) / 200;

      // Calculate new value with sensitivity
      let newValue = currentValue + deltaY * sensitivity;

      // Clamp to min/max
      newValue = Math.max(min, Math.min(max, newValue));

      // Update value
      setCurrentValue(newValue);
      onChange(newValue);

      // Reset start position
      setStartY(clientY);
    },
    [isDragging, disabled, startY, currentValue, min, max, onChange]
  );

  // Handle mouse/touch up event
  const handleDragEnd = () => {
    setIsDragging(false);
  };

  // Set up event listeners
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => handleDragMove(e.clientY);
    const handleMouseUp = () => handleDragEnd();
    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length > 0) {
        handleDragMove(e.touches[0].clientY);
      }
    };
    const handleTouchEnd = () => handleDragEnd();

    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.addEventListener("touchmove", handleTouchMove);
      document.addEventListener("touchend", handleTouchEnd);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
    };
  }, [isDragging, handleDragMove]);

  // Update internal state when prop value changes
  useEffect(() => {
    setCurrentValue(value);
  }, [value]);

  const rotationAngle = getRotationAngle();

  return (
    <DialContainer variant={variant} id={`dial-${label.toLowerCase()}`}>
      <DialWrapper
        ref={dialRef}
        $size={dialSize}
        $disabled={disabled}
        $isDragging={isDragging}
        onMouseDown={(e) => handleDragStart(e.clientY)}
        onTouchStart={(e) => {
          if (e.touches.length > 0) {
            handleDragStart(e.touches[0].clientY);
          }
        }}
      >
        {/* Outer dial background - fixed position, does NOT rotate */}
        <DialOuter src={dialOuter} alt="dial outer" $size={dialSize} />

        {/* Shadow - fixed position, does NOT rotate */}
        <DialShadow />

        {/* Inner dial - this rotates */}
        <DialInner $rotationAngle={rotationAngle}>
          <DialPointer />
        </DialInner>
      </DialWrapper>
      <DialInfo variant={variant}>
        <DialLabel variant={variant}>{label}</DialLabel>
        {!hideValue && (
          <DialValue variant={variant} $isCustom={showCustomStyle}>
            {valueFormatter(currentValue)}
          </DialValue>
        )}
      </DialInfo>
    </DialContainer>
  );
};

export default Dial;

// Styled Components
interface DialContainerProps {
  variant: "default" | "small";
}

const DialContainer = styled.div<DialContainerProps>`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: ${(props) => (props.variant === "small" ? "8px" : "10px")};
  width: ${(props) => (props.variant === "small" ? "40px" : "50px")};
`;

interface DialWrapperProps {
  $size: number;
  $disabled: boolean;
  $isDragging: boolean;
}

const DialWrapper = styled.div<DialWrapperProps>`
  position: relative;
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
  user-select: none;
  touch-action: none;
  transition: all 0.2s ease;
  width: ${(props) => props.$size}px;
  height: ${(props) => props.$size}px;
  opacity: ${(props) => (props.$disabled ? 0.6 : 1)};
`;

interface DialOuterProps {
  $size: number;
}

const DialOuter = styled.img<DialOuterProps>`
  position: absolute;
  pointer-events: none;
  width: ${(props) => props.$size * 1.5}px;
  height: ${(props) => props.$size * 1.25}px;
  top: ${(props) => -props.$size * 0.25}px;
  left: ${(props) => -props.$size * 0.25}px;
  /* Shadow stays fixed - no rotation applied */
`;

interface DialInnerProps {
  $rotationAngle: number;
}

const DialShadow = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  box-shadow: 3px 3px 8px rgba(0, 0, 0, 0.4), 2px 2px 4px rgba(0, 0, 0, 0.3),
    inset 0 1px 2px rgba(255, 255, 255, 0.8);
  z-index: 1;
  top: 50%;
  left: 50%;
  pointer-events: none;
`;

const DialInner = styled.div<DialInnerProps>`
  position: absolute;
  width: 100%;
  height: 100%;
  transform: translate(-50%, -50%) rotate(${(props) => props.$rotationAngle}deg);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  top: 50%;
  left: 50%;
`;

const DialPointer = styled.div`
  position: relative;
  width: 2px;
  height: 20%;
  background-color: #333;
  margin-bottom: 50%;
`;

interface DialInfoProps {
  variant: "default" | "small";
}

const DialInfo = styled.div<DialInfoProps>`
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

interface DialLabelProps {
  variant: "default" | "small";
}

const DialLabel = styled.div<DialLabelProps>`
  font-size: ${(props) => (props.variant === "small" ? "13px" : "14px")};
  color: #333;
  margin-bottom: 4px;
  font-weight: 500;
`;

interface DialValueProps {
  variant: "default" | "small";
  $isCustom?: boolean;
}

const DialValue = styled.div<DialValueProps>`
  font-size: ${(props) => (props.variant === "small" ? "14px" : "16px")};
  color: ${(props) => (props.$isCustom ? "#F58413" : "#333")};
  font-weight: bold;
  text-align: center;
`;
